/**
 * AgGrid Context Provider
 *
 * Provides AgGrid API utilities with pre-bound functions to child components.
 * Eliminates the need to pass GridApi instance manually to utility functions.
 */

import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

import {
  getCellValue as utilGetCellValue,
  getCellValueFromRow as utilGetCellValueFromRow,
  refreshCells as utilRefreshCells,
  getAllRowData as utilGetAllRowData,
  findRowById as utilFindRowById,
  type CellReference,
} from "../utilities/agGridUtils";

import type { GridApi } from "ag-grid-community";

/**
 * AgGrid context value with pre-bound utility functions
 */
export interface AgGridContextValue {
  /** Original GridApi instance for direct access when needed */
  api: GridApi | null;

  /** Get value from current row or specific field */
  getCellValue: (
    field: string,
    currentRowData?: Record<string, unknown>,
  ) => unknown;

  /** Get value from a specific row by ID */
  getCellValueFromRow: (rowId: string, field: string) => unknown;

  /** Refresh specific cells in the grid */
  refreshCells: (cells: CellReference[]) => void;

  /** Get all row data from the grid */
  getAllRowData: () => Record<string, unknown>[];

  /** Find row data by row ID */
  findRowById: (rowId: string) => Record<string, unknown> | null;

  /** Get total number of displayed rows */
  getDisplayedRowCount: () => number;
}

export const AgGridContext = createContext<AgGridContextValue | null>(null);

export interface AgGridProviderProps {
  /** GridApi instance from onGridReady */
  api: GridApi | null;
  children: ReactNode;
}

/**
 * Provider component that wraps AgGrid utilities with the current API instance
 */
export const AgGridProvider: React.FC<AgGridProviderProps> = ({
  api,
  children,
}) => {
  const contextValue = useMemo<AgGridContextValue | null>(() => {
    if (!api) {
      return null;
    }

    return {
      api,
      getCellValue: (field: string, currentRowData?: Record<string, unknown>) =>
        utilGetCellValue(api, field, currentRowData),
      getCellValueFromRow: (rowId: string, field: string) =>
        utilGetCellValueFromRow(api, rowId, field),
      refreshCells: (cells: CellReference[]) => utilRefreshCells(api, cells),
      getAllRowData: () => utilGetAllRowData(api),
      findRowById: (rowId: string) => utilFindRowById(api, rowId),
      getDisplayedRowCount: () => api.getDisplayedRowCount(),
    };
  }, [api]);

  return (
    <AgGridContext.Provider value={contextValue}>
      {children}
    </AgGridContext.Provider>
  );
};

/**
 * Hook to access AgGrid utilities with pre-bound functions
 * @throws Error if used outside of AgGridProvider
 */
export const useAgGridUtils = (): AgGridContextValue => {
  const context = useContext(AgGridContext);
  if (!context) {
    throw new Error(
      "useAgGridUtils must be used within an AgGridProvider. " +
        "Make sure the component is wrapped with <AgGridProvider api={gridApi}>.",
    );
  }
  return context;
};

/**
 * Convenience hook for cell operations
 */
export const useAgGridCellOperations = (): Pick<
  AgGridContextValue,
  "getCellValue" | "getCellValueFromRow" | "refreshCells"
> => {
  const { getCellValue, getCellValueFromRow, refreshCells } = useAgGridUtils();
  return { getCellValue, getCellValueFromRow, refreshCells };
};

/**
 * Convenience hook for data operations
 */
export const useAgGridData = (): Pick<
  AgGridContextValue,
  "getAllRowData" | "findRowById" | "getDisplayedRowCount"
> => {
  const { getAllRowData, findRowById, getDisplayedRowCount } = useAgGridUtils();
  return { getAllRowData, findRowById, getDisplayedRowCount };
};

/**
 * Hook to access the raw GridApi when needed for operations not covered by utilities
 */
export const useAgGridApi = (): GridApi | null => {
  const { api } = useAgGridUtils();
  return api;
};
